<template>
  <v-dialog @click:outside="handleCancel" v-model="dialog" max-width="500">
    <v-card>
      <div class="pa-md-10 pa-4">
        <Subtitle :fontSize="1.1" :fontWeight="900">
          {{ confirmationTitle }}
        </Subtitle>
        <TextBody customClass="mb-0">{{ confirmationText }}</TextBody>
      </div>
      <v-divider></v-divider>
      <v-card-actions class="px-md-10 px-4 py-4">
        <v-spacer class="d-md-block d-none"></v-spacer>
        <TextButton
          :style="buttonStyles"
          :label="cancelButtonLabel"
          @click="handleCancel"
        />
        <TextButton
          :style="buttonStyles"
          :label="confirmButtonLabel"
          color="primary"
          @click="handleConfirm"
        />
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  PropType,
  Ref,
  ref,
  WritableComputedRef,
} from "vue"
import Subtitle from "components/UI/Subtitle.vue"
import TextBody from "components/UI/TextBody.vue"
import TextButton from "components/UI/TextButton.vue"
import { ConfirmationService } from "features/common/confirmation-dialog/confirmation.service"
import useTranslate from "features/common/translate/concerns/useTranslate"
import { useIsMobile } from "features/common/vuetify/useVuetify"

const { t$ } = useTranslate("components.confirmation_dialog")

const DEFAULT_CONFIRMATION_TITLE = t$(".title")
const DEFAULT_CONFIRMATION_TEXT = t$(".text")
const DEFAULT_CONFIRM_BUTTON_LABEL = t$(".confirm")
const DEFAULT_CANCEL_BUTTON_LABEL = t$(".cancel")

export default defineComponent({
  name: "ConfirmationDialog",
  components: { TextButton, TextBody, Subtitle },
  props: {
    confirmationService: {
      type: Object as PropType<ConfirmationService>,
      required: true,
    },
  },
  setup(props) {
    const _dialog = ref(false)
    const dialog: WritableComputedRef<boolean> = computed({
      get: () => _dialog.value,
      set: (value) => (_dialog.value = value),
    })

    const confirmationTitle = ref(DEFAULT_CONFIRMATION_TITLE)
    const confirmationText = ref(DEFAULT_CONFIRMATION_TEXT)
    const confirmButtonLabel = ref(DEFAULT_CONFIRM_BUTTON_LABEL)
    const cancelButtonLabel = ref(DEFAULT_CANCEL_BUTTON_LABEL)

    const _reset = () => {
      confirmationTitle.value = DEFAULT_CONFIRMATION_TITLE
      confirmationText.value = DEFAULT_CONFIRMATION_TEXT
      confirmButtonLabel.value = DEFAULT_CONFIRM_BUTTON_LABEL
      cancelButtonLabel.value = DEFAULT_CANCEL_BUTTON_LABEL
    }

    const _handleConfirm: Ref<() => void> = ref(() => {
      //pass
    })
    const _handleCancel: Ref<() => void> = ref(() => {
      //pass
    })

    const _handleOpenConfirmation = (
      title?: string,
      text?: string,
      confirmText?: string,
      cancelText?: string
    ): Promise<boolean> => {
      confirmationTitle.value = title || DEFAULT_CONFIRMATION_TITLE
      confirmationText.value = text || DEFAULT_CONFIRMATION_TEXT
      confirmButtonLabel.value = confirmText || DEFAULT_CONFIRM_BUTTON_LABEL
      cancelButtonLabel.value = cancelText || DEFAULT_CANCEL_BUTTON_LABEL
      dialog.value = true
      return new Promise((resolve) => {
        const handleConfirm = () => {
          dialog.value = false
          resolve(true)
        }
        const handleCancel = () => {
          dialog.value = false
          resolve(false)
        }
        _handleConfirm.value = handleConfirm
        _handleCancel.value = handleCancel
      })
    }

    props.confirmationService.registerCallback(_handleOpenConfirmation)

    const handleConfirm = () => {
      _handleConfirm.value()
      _reset()
    }

    const handleCancel = () => {
      _handleCancel.value()
      _reset()
    }

    const isMobile = useIsMobile()
    const buttonStyles = computed(() => {
      return isMobile.value
        ? {
            flex: "1 1 0",
          }
        : {}
    })

    return {
      dialog,
      confirmationTitle,
      confirmationText,
      confirmButtonLabel,
      cancelButtonLabel,
      handleConfirm,
      handleCancel,
      buttonStyles,
    }
  },
})
</script>
